import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Checkbox, Grid, TableCell, TableRow } from '@mui/material';
import { TextField, IconButton, Autocomplete, Typography } from '@totalsoft/rocket-ui';
import { withParams } from 'providers/ParamsConsumer';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import Sync from '@mui/icons-material/Sync';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import WrappedLinearProgress from 'components/common/WrappedLinearProgress';

export const OfferFinancialServicesItemComponentExtendedFields = ({
  item,
  loading,
  category,
  onChange,
  editable,
  validationInfo,
  onServiceItemPropertyChange,
  onServiceItemComputeValue,
  deleteService,
  saving,
  disabled,
  params,
  t,
  categoryIndex,
  itemIndex,
  readOnlyFull,
  addDeleteAdditionalServices
}) => {
  const periodicities = [{ periodicity: 1 }, { periodicity: 3 }, { periodicity: 6 }, { periodicity: 12 }];

  const handleChange = event => {
    onChange(item.serviceTypeId, category ? category.serviceCategoryId : null, event);
  };

  const onDeleteService = () => {
    deleteService(item.serviceTypeId);
  };

  const handleFromInstallmentChange = value => {
    onServiceItemPropertyChange(item.serviceTypeId, category ? category.serviceCategoryId : null, 'fromInstallment', value);
    onServiceItemPropertyChange(item.serviceTypeId, category ? category.serviceCategoryId : null, 'period', item.toInstallment - value + 1);
  };

  const handleToInstallmentChange = value => {
    onServiceItemPropertyChange(item.serviceTypeId, category ? category.serviceCategoryId : null, 'toInstallment', value);
    onServiceItemPropertyChange(
      item.serviceTypeId,
      category ? category.serviceCategoryId : null,
      'period',
      value - item.fromInstallment + 1
    );
  };

  const handlePercentChange = value => {
    onServiceItemPropertyChange(item.serviceTypeId, category ? category.serviceCategoryId : null, 'percent', value);
  };
  const handleTotalValueChange = value => {
    onServiceItemPropertyChange(item.serviceTypeId, category ? category.serviceCategoryId : null, 'value', value);
  };

  const handlePeriodicityChange = value => {
    onServiceItemPropertyChange(item.serviceTypeId, category ? category.serviceCategoryId : null, 'periodicity', value.periodicity);
  };

  const evaluateCategoryIndex = categoryIndex => {
    if (categoryIndex !== null && categoryIndex !== undefined) return 'category_' + categoryIndex + '_item_' + itemIndex;
    else return 'item_' + itemIndex;
  };
  return (
    <>
      {loading === true ? (
        <Grid item xs={12}>
          <WrappedLinearProgress />
        </Grid>
      ) : (
        <TableRow hover>
          <TableCell width='30%'>
            <Checkbox
              id={'offerFinancialServicesCard_itemCard_' + evaluateCategoryIndex(categoryIndex) + '_isSelected'}
              checked={item.selected}
              onChange={handleChange}
              color={item.errorMessage ? 'error' : 'primary'}
              disabled={readOnlyFull || item.mandatory || loading || saving || disabled}
            />
            <Typography
              variant='caption'
              fontSize='16px'
              color={item.errorMessage ? 'error' : 'textPrimary'}
              id={'offerFinancialServicesCard_itemCard_' + evaluateCategoryIndex(categoryIndex) + '_name'}
            >
              {item.name}
            </Typography>
          </TableCell>
          <TableCell>
            <TextField
              id={'offerFinancialServicesCard_itemCard_' + evaluateCategoryIndex(categoryIndex) + '_value'}
              fullWidth
              isNumeric
              value={item.value || ''}
              onChange={handleTotalValueChange}
              disabled={item.readOnlyYN || readOnlyFull || !editable || !item.selected || disabled}
              startAdornment={
                !((item.mandatory && !item.errorMessage) || saving || readOnlyFull || (!item.canComputeService && !item.errorMessage)) && (
                  <>
                    {item.errorMessage && (
                      <Tooltip title={item.errorMessage}>
                        <PriorityHighIcon fontSize='small' color='error' />
                      </Tooltip>
                    )}
                    <IconButton
                      id={'offerFinancialServicesCard_computeBtn'}
                      color='primary'
                      variant='text'
                      size='tiny'
                      tooltip={t('OfferEdit.FinancialServices.ComputeService')}
                      onClick={onServiceItemComputeValue}
                    >
                      <Sync fontSize='small' />
                    </IconButton>
                  </>
                )
              }
            />
          </TableCell>
          <TableCell>
            <TextField
              id={'offerFinancialServicesCard_itemCard_' + evaluateCategoryIndex(categoryIndex) + '_monthlyValue'}
              fullWidth
              isNumeric
              value={!item.serviceMonthlyAmount && item.serviceMonthlyAmount !== 0 ? '' : item.serviceMonthlyAmount}
              disabled={true}
            />
          </TableCell>

          <TableCell>
            <TextField
              id={'offerFinancialServicesCard_itemCard_' + evaluateCategoryIndex(categoryIndex) + '_percent'}
              fullWidth
              isNumeric
              value={!item.percent && item.percent !== 0 ? '' : item.percent}
              onChange={handlePercentChange}
              inputProps={{ decimalScale: params.cvsChPercent }}
              disabled={item.readOnlyYN || readOnlyFull || !editable || !item.selected || disabled}
            />
          </TableCell>

          <TableCell>
            <Autocomplete
              fullWidth
              required
              labelKey='periodicity'
              valueKey='periodicity'
              value={item.periodicity || ''}
              isSearchable={false}
              isClearable={false}
              onChange={handlePeriodicityChange}
              disabled={item.readOnlyYN || readOnlyFull || !editable || !item.selected || disabled}
              options={periodicities}
              error={validationInfo && validationInfo.periodicity ? true : false}
              helperText={validationInfo && validationInfo.periodicity}
            ></Autocomplete>
          </TableCell>

          <TableCell>
            <TextField
              id={'offerFinancialServicesCard_itemCard_' + evaluateCategoryIndex(categoryIndex) + '_fromInstallment'}
              fullWidth
              isNumeric
              value={item.fromInstallment || ''}
              onChange={handleFromInstallmentChange}
              disabled={item.readOnlyYN || readOnlyFull || !editable || !item.selected || disabled}
              inputProps={{ decimalScale: 0 }}
              error={validationInfo && validationInfo.fromInstallment ? true : false}
              helperText={validationInfo && validationInfo.fromInstallment}
            />
          </TableCell>
          <TableCell>
            <TextField
              id={'offerFinancialServicesCard_itemCard_' + evaluateCategoryIndex(categoryIndex) + '_toInstallment'}
              fullWidth
              isNumeric
              value={item.toInstallment || ''}
              onChange={handleToInstallmentChange}
              disabled={item.readOnlyYN || readOnlyFull || !editable || !item.selected || disabled}
              inputProps={{ decimalScale: 0 }}
              error={validationInfo && validationInfo.toInstallment ? true : false}
              helperText={validationInfo && validationInfo.toInstallment}
            />
          </TableCell>

          <TableCell>
            <TextField
              id={'offerFinancialServicesCard_itemCard_' + evaluateCategoryIndex(categoryIndex) + '_period'}
              fullWidth
              isNumeric
              value={item.period || ''}
              disabled={true}
              onBlur={onServiceItemComputeValue}
              inputProps={{ decimalScale: 0 }}
              error={validationInfo && validationInfo.period ? true : false}
              helperText={validationInfo && validationInfo.period}
            />
          </TableCell>
          <TableCell width='5%'>
            <IconButton
              id={'offerFinancialServicesCard_deleteBtn'}
              onClick={onDeleteService}
              tooltip={t('OfferEdit.FinancialServices.DeleteService')}
              color='error'
              variant='text'
              size='small'
              disabled={item.mandatory || saving || readOnlyFull || !addDeleteAdditionalServices || disabled}
            >
              <DeleteIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

OfferFinancialServicesItemComponentExtendedFields.propTypes = {
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onServiceItemPropertyChange: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
  onServiceItemComputeValue: PropTypes.func.isRequired,
  deleteService: PropTypes.func.isRequired,
  validationInfo: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  category: PropTypes.object,
  categoryIndex: PropTypes.number,
  itemIndex: PropTypes.number.isRequired,
  readOnlyFull: PropTypes.bool.isRequired,
  params: PropTypes.object.isRequired,
  addDeleteAdditionalServices: PropTypes.bool
};

export default withTranslation()(withParams(OfferFinancialServicesItemComponentExtendedFields));
